import React, { useId } from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
} from '@mui/material';

/**
 * App-standard select dropdown with label and helper/error text.
 *
 * @param {Array<{ value: string|number, label: string, disabled?: boolean }>} options
 */
export default function Select({
  label,
  value,
  onChange,
  options = [],
  placeholder,
  helperText,
  error = false,
  disabled = false,
  fullWidth = true,
  size = 'small',
  name,
  id,
  required = false,
  displayEmpty = false,
  sx,
  ...rest
}) {
  const autoId = useId();
  const selectId = id || `app-select-${autoId}`;
  const labelId = `${selectId}-label`;
  const resolvedValue = value ?? '';
  const showPlaceholder = displayEmpty || Boolean(placeholder);
  const labelShrink = showPlaceholder ? true : undefined;

  return (
    <FormControl fullWidth={fullWidth} size={size} error={error} disabled={disabled} sx={sx}>
      {label && (
        <InputLabel id={labelId} required={required} shrink={labelShrink}>
          {label}
        </InputLabel>
      )}
      <MuiSelect
        labelId={label ? labelId : undefined}
        label={label}
        value={resolvedValue}
        onChange={onChange}
        name={name}
        id={selectId}
        displayEmpty={showPlaceholder}
        sx={{
          borderRadius: 'var(--radius-sm)',
          backgroundColor: 'var(--color-bg-paper)',
          fontSize: 'var(--font-size-sm)',
        }}
        {...rest}
      >
        {placeholder && (
          <MenuItem value="" disabled={!displayEmpty}>
            <em>{placeholder}</em>
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem
            key={String(option.value)}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </MenuItem>
        ))}
      </MuiSelect>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}
